import type { ColorThemeType } from '../enums/themes/color-theme-type.enum';
import type { IColorThemeType } from '../interfaces/color-theme-type.interface';
import { ConfigStore } from '../store/config';
import { get } from 'svelte/store';
import { lighten } from './hex-color-format';

export function getThemeSettings(themeName: ColorThemeType) {
  let configTheme: IColorThemeType;

  switch (themeName) {
    case 'THEME_1': {
      configTheme = {
        bgColor: '#fff',
        bgHeaderColor:
          'linear-gradient(90deg, #051AAA 0%, #081AE7 50%, #4F5AFE 100%)',
        bgAnswerColor: '#F3F3F3',
        bgQuestionColor:
          'linear-gradient(90deg, #051AAA 0%, #081AE7 50%, #4F5AFE 100%)',
        bgButtonColor: '#081AE7',
        textButtonColor: '#fff',
      };
      break;
    }
    case 'THEME_2': {
      configTheme = {
        bgColor: '#FFFFFF',
        bgHeaderColor:
          'linear-gradient(90deg, #6E30FF 0%, #6F5CF2 40%, #526DF2 67%, #4477F4 100%)',
        bgAnswerColor: '#F3F3F3',
        bgQuestionColor:
          'linear-gradient(90deg, #6E30FF 0%, #6F5CF2 40%, #526DF2 67%, #4477F4 100%)',
        bgButtonColor: '#6E30FF',
        textButtonColor: '#fff',
      };
      break;
    }
    case 'THEME_3': {
      configTheme = {
        bgColor: '#fff',
        bgHeaderColor: '#fff',
        bgAnswerColor: '#FFE1F6',
        bgQuestionColor: '#7D67FE',
        bgButtonColor: '#7D67FE',
        textButtonColor: '#FFE1F6',
      };
      break;
    }
    case 'THEME_4': {
      configTheme = {
        bgColor: '#fff',
        bgHeaderColor:
          'linear-gradient(90deg, #AA0C24 0%, #CD1936 50%, #F83655 100%)',
        bgAnswerColor: '#F3F3F3',
        bgQuestionColor:
          'linear-gradient(90deg, #AA0C24 0%, #CD1936 50%, #F83655 100%)',
        bgButtonColor: '#CD1936',
        textButtonColor: '#fff',
      };
      break;
    }

    case 'THEME_5': {
      configTheme = {
        bgColor: '#fff',
        bgHeaderColor:
          'linear-gradient(90deg, #360797 0%, #5905E9 50%, #8C47FF 100%)',
        bgAnswerColor: '#F3F3F3',
        bgQuestionColor:
          'linear-gradient(90deg, #360797 0%, #5905E9 50%, #8C47FF 100%)',
        bgButtonColor: '#5905E9',
        textButtonColor: '#fff',
      };
      break;
    }

    case 'THEME_6': {
      configTheme = {
        bgColor: '#000',
        bgHeaderColor: '#000',
        bgAnswerColor: '#9C8BD5',
        bgQuestionColor: '#D1D425',
        bgButtonColor: '#D1D425',
        textButtonColor: '#9C8BD5',
        textRatingColor: '#fff',
      };
      break;
    }

    case 'THEME_7': {
      configTheme = {
        bgColor: '#EAEAEA',
        bgHeaderColor: '#000',
        bgAnswerColor: '#fff',
        bgQuestionColor: '#000',
        bgButtonColor: '#000',
        textButtonColor: '#fff',
        textRatingColor: '#000',
      };
      break;
    }

    case 'THEME_8': {
      configTheme = {
        bgColor: '#fff',
        bgHeaderColor: 'linear-gradient(90deg, #8dc4f3 0%, #cad2f8 75%)',
        bgAnswerColor: '#f6f9f6',
        bgQuestionColor: 'linear-gradient(90deg, #8dc4f3 0%, #cad2f8 75%)',
        bgButtonColor: '#5e75e4',
        textButtonColor: '#000',
        textRatingColor: '#000',
      };
      break;
    }

    case 'MIXED': {
      configTheme = {
        bgColor: '#fff',
        bgHeaderColor: `linear-gradient(90deg,  ${get(ConfigStore)?.customTheme?.badgeBg}  0%, ${get(ConfigStore)?.customTheme?.badgeIconColor} 75%)`,
        bgAnswerColor: '#F3F3F3',
        bgQuestionColor: `linear-gradient(90deg, ${get(ConfigStore)?.customTheme?.badgeBg} 0%,  ${get(ConfigStore)?.customTheme?.badgeIconColor} 75%)`,
        bgButtonColor: get(ConfigStore)?.customTheme?.badgeBg || '',
        textButtonColor:
          get(ConfigStore)?.customTheme?.msgBubbleUserTextColor || '#fff',
        textRatingColor: '#000',
      };
      break;
    }

    case 'LIGHT': {
      configTheme = {
        bgColor: '#fff',
        bgHeaderColor: `linear-gradient(90deg,  ${lighten(get(ConfigStore)?.customTheme?.badgeBg, 0.3)}  20%, ${lighten(get(ConfigStore)?.customTheme?.badgeBg, 0.2)}  30%, ${get(ConfigStore)?.customTheme?.badgeBg} 50%)`,
        bgAnswerColor: '#F3F3F3',
        bgQuestionColor: `linear-gradient(90deg,  ${lighten(get(ConfigStore)?.customTheme?.badgeBg, 0.3)}  20%,${lighten(get(ConfigStore)?.customTheme?.badgeBg, 0.2)}  30%,  ${get(ConfigStore)?.customTheme?.badgeBg} 50%)`,
        bgButtonColor: get(ConfigStore)?.customTheme?.badgeBg || '',
        textButtonColor:
          get(ConfigStore)?.customTheme?.msgBubbleUserTextColor || '#fff',
        textRatingColor: '#000',
      };
      break;
    }

    default: {
      configTheme = {
        bgColor: get(ConfigStore)?.customTheme?.bodyBg || '#fff',
        bgHeaderColor:
          get(ConfigStore)?.customTheme?.headerBg ||
          'linear-gradient(141deg, rgb(227 227 227) 0%, rgb(223 223 223) 37%, rgb(219 219 219) 100%)',
        bgAnswerColor:
          get(ConfigStore)?.customTheme?.msgBubbleAgentBg || '#f0f0f0',
        bgQuestionColor:
          get(ConfigStore)?.customTheme?.msgBubbleUserBg || '#7123c0',
        bgButtonColor:
          get(ConfigStore)?.customTheme?.msgBubbleUserBg || '#7123C0',
        textButtonColor:
          get(ConfigStore)?.customTheme?.msgBubbleUserTextColor || '#fff',
        textRatingColor: '#000',
      };
      break;
    }
  }

  const vars = {
    '--cs-bg-color': configTheme?.bgColor,
    '--cs-bg-header-color': configTheme?.bgHeaderColor,
    '--cs-text-header-color': get(ConfigStore)?.customTheme?.headerTextColor || '#000',
    '--cs-bg-a-color': configTheme?.bgAnswerColor,
    '--cs-text-a-color': get(ConfigStore)?.customTheme?.msgBubbleAgentTextColor || '#000',
    '--cs-bg-q-color': configTheme?.bgQuestionColor,
    '--cs-text-q-color': get(ConfigStore)?.customTheme?.msgBubbleUserTextColor || '#fff',
    '--cs-bg-button-color': configTheme?.bgButtonColor,
    '--cs-text-button-color': configTheme?.textButtonColor,
    '--cs-text-rating-color': configTheme?.textRatingColor,
  };

  if (vars) {
    for (const [key, value] of Object.entries(vars)) {
      document.documentElement.style.setProperty(key, value ?? '');
    }
  }
}
